/**
 * How the grid and its cells talk through the DOM.
 *
 * A cell is rendered by the caller's column definitions, so it cannot reach
 * the grid through props. Instead the grid marks its scroller and the cells
 * mark their controls with data attributes, and the two pass custom events:
 * a cell editor tells the grid to move the cursor on (`cell-move`), and the
 * grid hands a typed key to the text cell under the cursor (`cell-type`).
 * The attribute names are also used by the grid's CSS in `src/index.css`.
 */

/** Spread onto the grid's scroller: the element that owns keyboard focus. */
export const gridRootProps = { 'data-grid-root': true } as const;
/** Spread onto a text cell's display, which typing on the cell opens. */
export const textCellProps = { 'data-text-cell': true } as const;
/** Spread onto the icon that opens a cell's list or editor on one click. */
export const openIconProps = { 'data-open-icon': true } as const;
/** Spread onto the grid cell under the cursor. */
export const activeCellProps = { 'data-active-cell': true } as const;

const GRID_ROOT = '[data-grid-root]';
const TEXT_CELL = '[data-text-cell]';
const OPEN_ICON = '[data-open-icon]';
const ACTIVE_CELL = '[data-active-cell]';

const CELL_MOVE = 'cell-move';
const CELL_TYPE = 'cell-type';

/** How far a cell editor sends the cursor when it commits. */
export type CellMove = { rows: number; cols: number };

export function findGridRoot(from: Element | null): HTMLElement | null {
  return from?.closest<HTMLElement>(GRID_ROOT) ?? null;
}

/** Give the keyboard back to the grid without scrolling it. */
export function focusGrid(root: HTMLElement | null): void {
  root?.focus({ preventScroll: true });
}

/** Whether the keys are with the grid, or with nothing in particular. */
export function focusIsWithGrid(root: HTMLElement | null): boolean {
  const focused = document.activeElement;
  return (
    focused === null || focused === document.body || !!root?.contains(focused)
  );
}

const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]';

/**
 * Tab past the grid's first or last cell: focus the next (or, backwards, the
 * previous) tabbable element outside the grid in document order, rather than
 * the grid's own header buttons and cell controls. False when there is none.
 */
export function focusOutsideGrid(
  root: HTMLElement,
  direction: 1 | -1
): boolean {
  const outside = Array.from(
    document.querySelectorAll<HTMLElement>(TABBABLE)
  ).filter(
    el =>
      el.tabIndex >= 0 && !el.hidden && !root.contains(el) && !el.contains(root)
  );
  const after = (el: HTMLElement) =>
    !!(root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
  const before = outside.filter(el => !after(el));
  const target =
    direction === 1 ? outside.find(after) : before[before.length - 1];
  if (!target) return false;
  target.focus();
  return true;
}

/** The text cell inside a grid cell, if that is what the cell holds. */
export function findTextCell(cell: Element | null): HTMLElement | null {
  return cell?.querySelector<HTMLElement>(TEXT_CELL) ?? null;
}

/** Whether `el` sits in the grid cell under the cursor. */
export function isInActiveCell(el: Element): boolean {
  return el.closest(ACTIVE_CELL) !== null;
}

/** Whether a click landed on a cell's one-click open icon. */
export function isOnOpenIcon(target: EventTarget): boolean {
  return target instanceof Element && target.closest(OPEN_ICON) !== null;
}

/** From a cell editor: move the grid's cursor by `move`. Bubbles to the grid. */
export function dispatchCellMove(from: Element | null, move: CellMove): void {
  from?.dispatchEvent(
    new CustomEvent<CellMove>(CELL_MOVE, { bubbles: true, detail: move })
  );
}

/** On the grid's scroller: listen for cell editors moving the cursor. */
export function onCellMove(
  root: HTMLElement,
  handler: (move: CellMove) => void
): () => void {
  const listener = (event: Event) =>
    handler((event as CustomEvent<CellMove>).detail);
  root.addEventListener(CELL_MOVE, listener);
  return () => root.removeEventListener(CELL_MOVE, listener);
}

/** From the grid: hand a typed key to a text cell, which opens with it. */
export function dispatchCellType(textCell: HTMLElement, key: string): void {
  textCell.dispatchEvent(new CustomEvent<string>(CELL_TYPE, { detail: key }));
}

/** On a text cell's display: listen for keys the grid hands it. */
export function onCellType(
  textCell: HTMLElement,
  handler: (key: string) => void
): () => void {
  const listener = (event: Event) =>
    handler((event as CustomEvent<string>).detail);
  textCell.addEventListener(CELL_TYPE, listener);
  return () => textCell.removeEventListener(CELL_TYPE, listener);
}
